import { EMBLEMS } from './emblems';

export type FactionId = 'miners' | 'pirates' | 'guards';

export type ShipVariantId = 'miner-hauler' | 'pirate-interceptor' | 'guard-cruiser';

export interface FactionPalette {
  readonly primary: number;
  readonly primaryDark: number;
  readonly hull: number;
  readonly hullDark: number;
  readonly accent: number;
  readonly trim: number;
  readonly cockpit: number;
  readonly cockpitGlow: number;
  readonly glow: number;
  readonly plasma: number;
  readonly strobe: number;
}

export interface FactionDefinition {
  readonly id: FactionId;
  readonly displayName: string;
  readonly tagline: string;
  readonly description: string;
  readonly doctrine: readonly string[];
  readonly uiColor: string;
  readonly uiGlow: string;
  readonly emblem: string;
  readonly shipVariant: ShipVariantId;
  readonly palette: FactionPalette;
  readonly colorChoices: readonly number[];
  readonly defaultColor: number;
}

export const FACTIONS: Record<FactionId, FactionDefinition> = {
  miners: {
    id: 'miners',
    displayName: 'Miners',
    tagline: 'We extract the core of the sector',
    description:
      'Heavy haulers built to crack asteroids and drain crystal from the deep veins. Maximum cargo, thick armor, and pride in every ton delivered.',
    doctrine: [
      'More cargo',
      'More armor',
      'Control of extraction zones'
    ],
    uiColor: '#ffb548',
    uiGlow: 'rgba(255, 181, 72, 0.45)',
    emblem: EMBLEMS.miners,
    shipVariant: 'miner-hauler',
    palette: {
      primary: 0xf0a23c,
      primaryDark: 0x8a4a14,
      hull: 0xe9dcc0,
      hullDark: 0x5b4a36,
      accent: 0xffd873,
      trim: 0x231c14,
      cockpit: 0x0d0e16,
      cockpitGlow: 0xffb55a,
      glow: 0xffd873,
      plasma: 0xff9a3c,
      strobe: 0xffd27a
    },
    colorChoices: [
      0xf0a23c, 0xd85f2a, 0xc7922b, 0x7fbf5a, 0x4fa2c9, 0x8e6fd1
    ],
    defaultColor: 0xf0a23c
  },
  pirates: {
    id: 'pirates',
    displayName: 'Pirates',
    tagline: 'What is yours is also ours',
    description:
      'Agile, light, sharp interceptors. They ambush haulers, dominate dark routes, and live off the weakness of others. Speed is the only code.',
    doctrine: [
      'Extreme speed',
      'High mobility',
      'Hit and run'
    ],
    uiColor: '#ff5a6a',
    uiGlow: 'rgba(255, 90, 106, 0.45)',
    emblem: EMBLEMS.pirates,
    shipVariant: 'pirate-interceptor',
    palette: {
      primary: 0xd43a3a,
      primaryDark: 0x841e1e,
      hull: 0xebe6d8,
      hullDark: 0x6e6256,
      accent: 0xf2c048,
      trim: 0x2a2838,
      cockpit: 0x0c0d1a,
      cockpitGlow: 0x5a82c4,
      glow: 0x9cdcff,
      plasma: 0x6ab6ff,
      strobe: 0xff7a7a
    },
    colorChoices: [
      0xd43a3a, 0x2c2c3a, 0x6b1a7a, 0xc14a18, 0x1d6b3a, 0xc7a528
    ],
    defaultColor: 0xd43a3a
  },
  guards: {
    id: 'guards',
    displayName: 'Guards',
    tagline: 'The law walks the routes',
    description:
      'Balanced cruisers, solid defense, and imposing presence. They escort, patrol, and hunt pirates. They hold strategic points as if order depended on it.',
    doctrine: [
      'Solid defense',
      'Area control',
      'Escort and pirate hunting'
    ],
    uiColor: '#6ac0ff',
    uiGlow: 'rgba(106, 192, 255, 0.45)',
    emblem: EMBLEMS.guards,
    shipVariant: 'guard-cruiser',
    palette: {
      primary: 0x3f7fc4,
      primaryDark: 0x1a3a66,
      hull: 0xdce6f2,
      hullDark: 0x4a5668,
      accent: 0xd5ecff,
      trim: 0x1a1e2c,
      cockpit: 0x0a0c18,
      cockpitGlow: 0x9ad0ff,
      glow: 0xc6e6ff,
      plasma: 0x6ac0ff,
      strobe: 0x6ac0ff
    },
    colorChoices: [
      0x3f7fc4, 0x2a9d8f, 0x355e8a, 0xb0b7c6, 0x2c3e50, 0xf4a261
    ],
    defaultColor: 0x3f7fc4
  }
};

export const FACTION_ORDER: readonly FactionId[] = ['miners', 'pirates', 'guards'];

export interface FactionSelection {
  readonly factionId: FactionId;
  readonly primaryColor: number;
}

export function getFaction(id: FactionId): FactionDefinition {
  return FACTIONS[id];
}

export function defaultSelection(): FactionSelection {
  return {
    factionId: 'pirates',
    primaryColor: FACTIONS.pirates.defaultColor
  };
}
